import React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { StatisticsSummaryView } from '../../api/generated/models/StatisticsSummaryView';
import { getDisplayCurrency, getAmountForCurrency, getAverageOrderValue, formatMoney } from '../../utils/statisticsHelpers';

interface AverageCheckWidgetProps {
  data: StatisticsSummaryView | null;
}

/**
 * ТЗ п.7: Average Check (basket value) - bar chart by day + comparison with previous period.
 */
export const AverageCheckWidget: React.FC<AverageCheckWidgetProps> = ({ data }) => {
  const { t } = useTranslation();
  const currency = getDisplayCurrency(data);
  const avgVal = getAverageOrderValue(data);
  const avg = avgVal?.averageByCurrency?.[currency] ?? 0;
  const prevAvg = avgVal?.previousPeriodAverageByCurrency?.[currency];

  // Compute % change vs previous period
  const changePct = prevAvg != null && prevAvg > 0 ? ((avg - prevAvg) / prevAvg) * 100 : null;

  const chartData = React.useMemo(() => {
    const byDay = data?.salesByDay ?? [];
    if (byDay.length === 0) return [{ name: '-', value: avg }];
    return byDay.map((d: any) => ({
      name: d.date,
      value: d.orderCount > 0 ? getAmountForCurrency(d.amountByCurrency, currency) / d.orderCount : 0,
    }));
  }, [data, currency, avg]);

  return (
    <div className="stat-card">
      <div className="stat-card-header">
        <h3 className="stat-card-title">{t('dashboard_stats_average_check')}</h3>
      </div>
      <div className="stat-metric stat-metric--mb">
        <span className="stat-metric-label">{t('dashboard_stats_avg')}</span>
        <span className="stat-metric-value">{formatMoney(avg, currency)}</span>
        {changePct != null && (
          <span className={`stat-metric-change ${changePct >= 0 ? 'positive' : 'negative'}`}>
            {changePct >= 0 ? '\u2191' : '\u2193'} {Math.abs(changePct).toFixed(1)}%
          </span>
        )}
        {prevAvg != null && (
          <span className="stat-metric-label">
            {t('dashboard_stats_compared_to_prev', { value: formatMoney(prevAvg, currency) })}
          </span>
        )}
      </div>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#6B7280" />
            <YAxis tick={{ fontSize: 10 }} stroke="#6B7280" />
            <Tooltip formatter={(value: number) => [formatMoney(value, currency), '']} />
            <Bar dataKey="value" fill="#059669" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
